/**
 * textimage.js
 * Burning a line of text onto a picture.
 *
 * Why this exists: a photograph scrolls past, a photograph with a hook on it
 * gets read. TikTok photo posts, Pinterest pins and Instagram carousels all
 * live or die on the first line, and until now that meant opening an image
 * editor for every picture.
 *
 * Everything is measured as a fraction of the picture's width, never in fixed
 * pixels. The same settings then look identical on a 1080px square and a
 * 4000px photograph, and the preview on screen — drawn small — is a true
 * likeness of the full-size render.
 *
 * The maths is separate from the drawing so it can be tested without a canvas.
 *
 * Dependencies: none (a canvas, at the bottom)
 */

/**
 * The parts of the screen each app covers with its own interface.
 *
 * Text underneath these is not hidden by cropping — it is hidden behind the
 * caption, the buttons and the username, which is exactly the disappearing
 * text people notice only after posting. Fractions of width and height.
 */
export const SAFE_AREAS = {
  none: { label: 'No guides', top: 0.04, right: 0.04, bottom: 0.04, left: 0.04 },
  tiktok: { label: 'TikTok', top: 0.1, right: 0.18, bottom: 0.24, left: 0.04 },
  instagram: { label: 'Instagram', top: 0.1, right: 0.06, bottom: 0.18, left: 0.04 },
  pinterest: { label: 'Pinterest', top: 0.05, right: 0.05, bottom: 0.12, left: 0.05 },
};

/** Headline height as a fraction of the picture's width. */
export const TEXT_SIZES = {
  small: { label: 'Small', scale: 0.055 },
  medium: { label: 'Medium', scale: 0.075 },
  large: { label: 'Large', scale: 0.1 },
};

export const POSITIONS = { top: 'Top', middle: 'Middle', bottom: 'Bottom' };

/**
 * What goes behind the text.
 *
 * White text on a pale photograph is unreadable, and that is not obvious in a
 * small preview. A shade is the difference between a hook and a smudge.
 */
export const SHADES = {
  none: 'None',
  dark: 'Dark panel',
  light: 'Light panel',
  band: 'Full-width band',
};

/** The usable box once the app's own interface is accounted for. */
export function safeBox({ width, height, area = SAFE_AREAS.none }) {
  const left = Math.round(width * area.left);
  const right = Math.round(width * area.right);
  const top = Math.round(height * area.top);
  const bottom = Math.round(height * area.bottom);

  return {
    x: left,
    y: top,
    width: Math.max(1, width - left - right),
    height: Math.max(1, height - top - bottom),
  };
}

/**
 * Splits text into lines that fit.
 *
 * `measure` is passed in rather than a canvas, so the wrapping can be tested
 * with a simple width-per-character stand-in. Explicit line breaks are kept:
 * somebody who pressed Enter meant it.
 */
export function wrapLines(text, maxWidth, measure) {
  const lines = [];

  for (const paragraph of String(text ?? '').split('\n')) {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      if (paragraph.trim() === '' && lines.length) lines.push('');
      continue;
    }

    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (line && measure(candidate) > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) lines.push(line);
  }

  return lines;
}

/**
 * Where the block of text sits inside the safe box.
 *
 * @returns {{x: number, y: number, width: number, height: number}} the block,
 *   with y as the top of the first line's box
 */
export function layoutBlock({ box, lineHeights, gap = 0, position = 'bottom' }) {
  const height = lineHeights.reduce((sum, h) => sum + h, 0) + gap * Math.max(0, lineHeights.length - 1);

  let y = box.y + box.height - height;
  if (position === 'top') y = box.y;
  else if (position === 'middle') y = box.y + (box.height - height) / 2;

  return { x: box.x, y: Math.max(box.y, y), width: box.width, height };
}

/** A rounded rectangle, for the panel behind the text. */
function roundedRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
  ctx.fill();
}

/**
 * Draws the text onto a canvas that already holds the picture.
 *
 * Used by both the live preview and the final render, at different sizes, so
 * what you see is what you get.
 */
export function drawTextOn(ctx, options) {
  const {
    width,
    height,
    headline = '',
    sub = '',
    position = 'bottom',
    size = 'medium',
    colour = '#ffffff',
    shade = 'dark',
    align = 'left',
    area = SAFE_AREAS.none,
    guides = false,
  } = options;

  const box = safeBox({ width, height, area });

  if (guides) {
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.setLineDash([Math.max(2, width * 0.012), Math.max(2, width * 0.012)]);
    ctx.lineWidth = Math.max(1, width * 0.004);
    ctx.strokeRect(box.x, box.y, box.width, box.height);
    ctx.restore();
  }

  const headlineSize = Math.round(width * (TEXT_SIZES[size]?.scale ?? TEXT_SIZES.medium.scale));
  const subSize = Math.round(headlineSize * 0.55);
  const gap = Math.round(headlineSize * 0.18);
  const pad = Math.round(headlineSize * 0.45);

  const headlineFont = `700 ${headlineSize}px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
  const subFont = `500 ${subSize}px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;

  ctx.font = headlineFont;
  const headlineLines = wrapLines(headline, box.width - pad * 2, (t) => ctx.measureText(t).width);

  ctx.font = subFont;
  const subLines = sub ? wrapLines(sub, box.width - pad * 2, (t) => ctx.measureText(t).width) : [];

  if (headlineLines.length === 0 && subLines.length === 0) return;

  const lineHeights = [
    ...headlineLines.map(() => Math.round(headlineSize * 1.22)),
    ...subLines.map(() => Math.round(subSize * 1.35)),
  ];

  const block = layoutBlock({ box, lineHeights, gap: 0, position });

  // The panel, drawn first so the text sits on top of it.
  if (shade !== 'none') {
    ctx.save();
    ctx.fillStyle = shade === 'light' ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.55)';

    if (shade === 'band') {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, block.y - pad, width, block.height + pad * 2);
    } else {
      const widest = Math.max(
        ...headlineLines.map((l) => {
          ctx.font = headlineFont;
          return ctx.measureText(l).width;
        }),
        ...subLines.map((l) => {
          ctx.font = subFont;
          return ctx.measureText(l).width;
        }),
        0,
      );
      const panelWidth = Math.min(box.width, widest + pad * 2);
      const panelX =
        align === 'centre' ? box.x + (box.width - panelWidth) / 2 : box.x;
      roundedRect(ctx, panelX, block.y - pad, panelWidth, block.height + pad * 2, pad * 0.6);
    }
    ctx.restore();
  }

  ctx.save();
  ctx.fillStyle = colour;
  ctx.textBaseline = 'top';
  ctx.textAlign = align === 'centre' ? 'center' : 'left';

  // A soft shadow keeps light text readable where the panel is off.
  if (shade === 'none') {
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = Math.round(headlineSize * 0.25);
    ctx.shadowOffsetY = Math.round(headlineSize * 0.04);
  }

  const textX = align === 'centre' ? box.x + box.width / 2 : box.x + pad;
  let y = block.y;

  ctx.font = headlineFont;
  for (const line of headlineLines) {
    ctx.fillText(line, textX, y);
    y += Math.round(headlineSize * 1.22);
  }

  ctx.font = subFont;
  for (const line of subLines) {
    ctx.fillText(line, textX, y);
    y += Math.round(subSize * 1.35);
  }

  ctx.restore();
}

/** "hook-photo.jpg" — obvious in the library six weeks later. */
export function textImageName(name) {
  const clean = String(name ?? 'picture.jpg');
  return clean.startsWith('hook-') ? clean : `hook-${clean}`;
}

/**
 * The finished picture, at its original size, as a new file.
 *
 * The original is never touched: this returns a new File, and the caller
 * decides whether to keep it.
 */
export async function renderTextImage(file, options) {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0);
  drawTextOn(ctx, { ...options, width: bitmap.width, height: bitmap.height, guides: false });
  bitmap.close?.();

  // PNG keeps text edges crisp; a JPEG source stays a JPEG so nothing balloons.
  const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('The browser could not draw that picture.'))), mime, 0.92);
  });

  const name = textImageName(file.name).replace(/\.[^.]+$/, mime === 'image/png' ? '.png' : '.jpg');
  return new File([blob], name, { type: mime });
}
